import Dexie, { type EntityTable } from 'dexie'
import type { DeviceBenchmark, SolverConfig, SolverResult, TrainerProgress } from './types/solver'

interface AppSetting {
  key: string
  value: unknown
}

class PokerSolverDatabase extends Dexie {
  configs!: EntityTable<SolverConfig, 'id'>
  results!: EntityTable<SolverResult, 'id'>
  benchmarks!: EntityTable<DeviceBenchmark, 'id'>
  settings!: EntityTable<AppSetting, 'key'>

  constructor() {
    super('iphone-gto-solver')
    this.version(1).stores({
      configs: 'id, createdAt, preset',
      results: 'id, createdAt, metadata.completed',
      benchmarks: 'id, createdAt',
      settings: 'key',
    })
  }
}

export const db = new PokerSolverDatabase()

export async function saveSolverResult(result: SolverResult): Promise<void> {
  await db.transaction('rw', db.configs, db.results, async () => {
    await db.configs.put(result.config)
    await db.results.put(result)
  })
}

export async function listSolverResults(): Promise<SolverResult[]> {
  return db.results.orderBy('createdAt').reverse().toArray()
}

export async function deleteSolverResult(id: string): Promise<void> {
  await db.transaction('rw', db.configs, db.results, async () => {
    await db.results.delete(id)
    await db.configs.delete(id)
  })
}

const EMPTY_TRAINER_PROGRESS: TrainerProgress = {
  totalAnswers: 0,
  correctAnswers: 0,
  closeAnswers: 0,
  incorrectAnswers: 0,
  totalEvLossBb: 0,
  evMeasuredAnswers: 0,
  completedSessions: 0,
  byCategory: {},
  recentQuestionIds: [],
  updatedAt: '',
}

export async function getTrainerProgress(): Promise<TrainerProgress> {
  const setting = await db.settings.get('trainer-progress')
  return setting?.value
    ? { ...EMPTY_TRAINER_PROGRESS, ...(setting.value as Partial<TrainerProgress>) }
    : { ...EMPTY_TRAINER_PROGRESS }
}

export async function recordTrainerAnswer(
  verdict: 'correct' | 'close' | 'incorrect',
  evLossBb?: number,
  category = 'postflop:unknown',
  questionId?: string,
): Promise<TrainerProgress> {
  const current = await getTrainerProgress()
  const categoryProgress = current.byCategory[category] ?? { answers: 0, correct: 0, close: 0, incorrect: 0 }
  const next: TrainerProgress = {
    ...current,
    totalAnswers: current.totalAnswers + 1,
    correctAnswers: current.correctAnswers + (verdict === 'correct' ? 1 : 0),
    closeAnswers: current.closeAnswers + (verdict === 'close' ? 1 : 0),
    incorrectAnswers: current.incorrectAnswers + (verdict === 'incorrect' ? 1 : 0),
    totalEvLossBb: current.totalEvLossBb + (evLossBb ?? 0),
    evMeasuredAnswers: current.evMeasuredAnswers + (evLossBb === undefined ? 0 : 1),
    byCategory: {
      ...current.byCategory,
      [category]: {
        answers: categoryProgress.answers + 1,
        correct: categoryProgress.correct + (verdict === 'correct' ? 1 : 0),
        close: categoryProgress.close + (verdict === 'close' ? 1 : 0),
        incorrect: categoryProgress.incorrect + (verdict === 'incorrect' ? 1 : 0),
      },
    },
    recentQuestionIds: questionId
      ? [questionId, ...current.recentQuestionIds.filter((id) => id !== questionId)].slice(0, 120)
      : current.recentQuestionIds,
    updatedAt: new Date().toISOString(),
  }
  await db.settings.put({ key: 'trainer-progress', value: next })
  return next
}

export async function recordTrainerSessionComplete(): Promise<TrainerProgress> {
  const current = await getTrainerProgress()
  const next = {
    ...current,
    completedSessions: current.completedSessions + 1,
    updatedAt: new Date().toISOString(),
  }
  await db.settings.put({ key: 'trainer-progress', value: next })
  return next
}
